import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { SandboxedWebComponent } from '../../../../../../../../../../standalone/web-components';
import { CUSTOM_COMPONENT_DATA } from '../../../../custom-components/custom-component-data.provider';
import { FiltersChannel } from '../../../../filters/models/filters-channel.model';
import { ITileInstance } from '../../../interfaces/tiles/definition-helpers/tile-definition.interface';

@Component({
  standalone: true,
  selector: 'custom-component-tile',
  imports: [SandboxedWebComponent],
  template: `<sandboxed-web-component [data]="customComponentData()" [src]="src()" />`,
})
export class CustomComponentTile {
  private readonly baseCustomComponentData = inject(CUSTOM_COMPONENT_DATA) as {
    message: string;
  };

  readonly tileInstance = input.required<ITileInstance>();

  readonly src = input.required<string>();

  readonly editMode = input<boolean>();

  readonly filtersChannel = input<FiltersChannel>();

  private readonly filtersSnapshot = signal<unknown>(null);

  private readonly filtersVersion = signal(0);

  constructor() {
    effect(
      (onCleanup) => {
        const channel = this.filtersChannel();

        if (!channel) {
          this.filtersSnapshot.set(null);
          this.filtersVersion.set(0);
          return;
        }

        const refreshFilters = () => {
          const nextFilters = this.getActiveFilters(channel);
          this.filtersSnapshot.set(this.cloneSerializable(nextFilters));
          this.filtersVersion.update((value) => value + 1);
        };

        refreshFilters();

        const subscription = new Subscription();
        subscription.add(channel.selectedFiltersSubject.subscribe(() => refreshFilters()));
        subscription.add(channel.filters.subscribe(() => refreshFilters()));

        onCleanup(() => subscription.unsubscribe());
      },
      { allowSignalWrites: true },
    );
  }

  protected readonly customComponentData = computed(() => {
    const tileInstance = this.tileInstance() as {
      config?: Record<string, unknown>;
    };

    return {
      ...this.baseCustomComponentData,
      config: tileInstance?.config,
      filters: this.filtersSnapshot(),
      filtersQuery: (this.filtersSnapshot() as { query?: unknown } | null)?.query ?? null,
      filtersVersion: this.filtersVersion(),
    };
  });

  private getActiveFilters(channel: FiltersChannel): unknown {
    return channel.filters?.getValue?.() ?? channel.selectedFilters ?? null;
  }

  private cloneSerializable<T>(value: T): T {
    if (value === null || value === undefined) {
      return value;
    }

    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value;
    }
  }
}
